import classNames from "classnames";
import ConditionalToolTip from "components/design/conditionalToolTip";
import Async from "components/DesignSystem/AsyncComponents/Async";
import Breadcrumb from "components/DesignSystem/Breadcrumb/Breadcrumb";
import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import { ArrowRight } from "components/icons/ArrowRight";
import { FILE_FOLDER_MOVED } from "constants/analyticsEvents";
import { NEW_FOLDER_MESSAGE } from "constants/documents";
import { useAnalytics } from "hooks/useAnalytics";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useModal } from "hooks/useModal";
import { useToast } from "hooks/useToast";
import { useEffect, useState } from "react";
import EmptyFolder from "static/images/EmptyFolder.svg";
import {
  useCreateFolderMutation,
  useGetFolderContentQuery,
  useMoveDocItemsMutation,
} from "store/apis/documents";
import { BackendError } from "types/utils/error";
import { ModalProps } from "types/utils/modal";
import { FileIcon } from "components/design/FileIcon";
import { AddNewFolder } from "./AddNewFolder";
import { MovePrivateFileToPublicAlert } from "./MovePrivateFileToPublicAlert";

type MoveDocModalProps = {
  itemIds: string[];
  onMoveSuccess?: () => void;
  currentFolderIds?: string;
  groupId?: string;
  documentsGroupId?: string;
  handleMoveItems?: (destinationFolderId: string, folderName?: string) => void;
  isMovingFile?: boolean;
  containsPrivateDocuments?: boolean;
} & ModalProps;

const Loader = () => {
  return (
    <div className="t-flex t-w-full t-items-center t-gap-2 t-px-2 t-py-1 t-group">
      <div className="t-bg-neutral-10 t-h-10 t-w-10 t-animate-pulse-fast" />
      <div className="t-bg-neutral-10 t-h-10 t-w-full t-animate-pulse-fast" />
    </div>
  );
};

export const MoveDocModal = ({
  isOpen,
  close,
  itemIds,
  onMoveSuccess,
  currentFolderIds,
  groupId: propGroupId,
  documentsGroupId: propDocumentsGroupId,
  handleMoveItems,
  isMovingFile,
  containsPrivateDocuments,
}: MoveDocModalProps) => {
  const { alertToast, successToast, info: infoToast } = useToast();
  const { trackEvent } = useAnalytics();
  const { uuid: groupId, documents_group_id } = useCurrentGroupContext();
  const [folderId, setFolderId] = useState(
    propDocumentsGroupId || documents_group_id
  );
  const [selectedFolder, setSelectedFolder] = useState({
    uuid: "",
    name: "",
    isPublic: true,
  });
  const [createFolder] = useCreateFolderMutation();
  const [moveDocItems, { isLoading: isMoving }] = useMoveDocItemsMutation();
  const groupUuid = propGroupId || groupId;
  const privateToPublicMoveAlert = useModal();

  const { data, isLoading, isSuccess, isFetching } = useGetFolderContentQuery(
    {
      folderId: folderId!,
      groupId: groupUuid!,
    },
    {
      skip: !folderId || !groupUuid,
      refetchOnMountOrArgChange: true,
    }
  );

  useEffect(() => {
    if (documents_group_id && !folderId) {
      setFolderId(documents_group_id);
    }
  }, [documents_group_id, folderId]);

  const { parents_list = [], subfolders = [], files = [] } = data || {};
  const folderItems = [...subfolders, ...files];
  const currentFolder = parents_list?.at(-1);
  const addNewFolder = useModal();
  const isSourceSameAsTarget = currentFolderIds?.includes(
    selectedFolder.uuid || folderId
  );

  const onCreateFolder = async ({ folder_name }: { folder_name: string }) => {
    if (!currentFolder?.uuid) {
      return;
    }
    try {
      await createFolder({
        folder_name,
        groupId: groupUuid,
        parentFolderId: currentFolder?.uuid,
      }).unwrap();
      successToast({ message: NEW_FOLDER_MESSAGE });
      addNewFolder.close();
    } catch (error) {
      alertToast(
        { message: (error as BackendError).data?.error?.message },
        error as Error
      );
    }
  };

  const onFolderClick = (folderId: string, is_private?: boolean) => {
    setFolderId(folderId);
    setSelectedFolder({
      uuid: "",
      name: "",
      isPublic: !is_private,
    });
  };

  const moveItems = async () => {
    const destination_folder_id = selectedFolder.uuid || folderId;

    if (handleMoveItems) {
      handleMoveItems(
        destination_folder_id,
        selectedFolder.name || currentFolder?.name
      );
      return;
    }

    try {
      await moveDocItems({
        destination_folder_id,
        file_uuids: itemIds,
        groupId: groupUuid,
      }).unwrap();
      onMoveSuccess?.();
      trackEvent(FILE_FOLDER_MOVED, {
        file_or_folder_ids: itemIds,
        destination_folder_id: destination_folder_id,
      });
      successToast({
        message: `File moved to ${selectedFolder.name || currentFolder?.name}`,
      });
      close();
    } catch (error) {
      alertToast(
        { message: (error as BackendError).data?.error?.message },
        error as Error
      );
    }
  };

  const handleMove = async () => {
    if (containsPrivateDocuments && selectedFolder.isPublic) {
      privateToPublicMoveAlert.open();
    } else {
      moveItems();
    }
  };

  const onClose = () => {
    infoToast({ message: "Move cancelled" });
    close();
  };

  const tooltipMessage = isSourceSameAsTarget
    ? "You have selected the same folder. Please select a different folder."
    : `Move to ${
        selectedFolder.uuid ? selectedFolder.name : currentFolder?.name
      }`;

  const isMoveInProgress = Boolean(isMoving || isMovingFile);

  return (
    <>
      <Modal.Root open={isOpen} onOpenChange={onClose}>
        <Modal.Content>
          <Modal.Header>
            <div>
              <Modal.Title>
                <div className="t-line-clamp-1">
                  {isLoading ? "Loading..." : currentFolder?.name}
                </div>
              </Modal.Title>
              {isSuccess && (
                <Modal.Subtitle>
                  <Breadcrumb.Root itemsToDisplay={3}>
                    {parents_list.map(({ name, uuid, is_private }) => (
                      <Breadcrumb.Item
                        key={uuid}
                        as="button"
                        onClick={() => onFolderClick(uuid, is_private)}
                      >
                        <div className="t-line-clamp-1">{name}</div>
                      </Breadcrumb.Item>
                    ))}
                  </Breadcrumb.Root>
                </Modal.Subtitle>
              )}
            </div>
            <Modal.Close />
          </Modal.Header>
          <Modal.Body className="t-h-[50dvh]">
            <Button size="small" onClick={addNewFolder.open}>
              New Folder
            </Button>
            <Async.Root
              isLoading={isFetching}
              isEmpty={folderItems.length === 0}
              isSuccess={isSuccess}
              customLoader={
                <div className="t-flex t-flex-col t-gap-1 t-mt-2">
                  {Array.from({ length: 6 }).map((_, index) => (
                    <Loader key={index} />
                  ))}
                </div>
              }
            >
              <Async.Empty>
                <div className="t-flex t-justify-center t-items-center t-flex-col t-gap-4 t-h-full">
                  <img
                    src={EmptyFolder}
                    alt="empty folder"
                    className="t-w-44 t-h-36"
                  />
                  <h4 className="t-text-body-sm t-text-text-30">
                    This folder is empty
                  </h4>
                </div>
              </Async.Empty>
              <Async.Success>
                <div className="t-flex t-flex-col t-gap-1 t-mt-2">
                  {folderItems.map(
                    ({ uuid, name, file_type, type, is_private }) => (
                      <button
                        disabled={type === "FILE"}
                        key={uuid}
                        onClick={(e) =>
                          setSelectedFolder({
                            uuid,
                            name,
                            isPublic: !is_private,
                          })
                        }
                        onDoubleClick={(e) => onFolderClick(uuid)}
                        className={classNames(
                          "all:unset t-flex t-w-full t-items-center t-gap-2 t-px-2 t-py-1 t-rounded disabled:t-text-neutral-50 t-text-text-100 t-text-body-sm disabled:t-opacity-50 disabled:t-pointer-events-none t-group",
                          {
                            "t-bg-surface-purple ":
                              selectedFolder.uuid === uuid,
                            "hover:t-bg-surface-lighter-grey focus-visible:t-bg-surface-lighter-grey focus-visible:t-outline focus-visible:t-outline-surface-grey":
                              selectedFolder.uuid !== uuid,
                          }
                        )}
                      >
                        <FileIcon fileType={file_type} size="32" />
                        <div className="t-line-clamp-1">{name}</div>
                        {type === "FOLDER" && (
                          <button
                            className="t-ml-auto t-text-purple-70 t-hidden group-hover:t-block t-rounded-full hover:t-bg-purple-20 all:unset t-items-center"
                            onClick={(e) => {
                              e.stopPropagation();
                              onFolderClick(uuid);
                            }}
                            onDoubleClick={(e) => {
                              e.stopPropagation();
                            }}
                          >
                            <ArrowRight
                              size="32"
                              stroke="1.4"
                              color="currentColor"
                            />
                          </button>
                        )}
                      </button>
                    )
                  )}
                </div>
              </Async.Success>
            </Async.Root>
          </Modal.Body>
          <Modal.FooterButtonGroup>
            <ConditionalToolTip condition={tooltipMessage}>
              <Button
                customType="primary"
                disabled={isSourceSameAsTarget || isMoveInProgress}
                isLoading={isMoveInProgress}
                onClick={handleMove}
              >
                Move Here
              </Button>
            </ConditionalToolTip>
          </Modal.FooterButtonGroup>
        </Modal.Content>
      </Modal.Root>
      {addNewFolder.isOpen && (
        <AddNewFolder
          isOpen={addNewFolder.isOpen}
          close={addNewFolder.close}
          createFolder={onCreateFolder}
        />
      )}
      {privateToPublicMoveAlert.isOpen && (
        <MovePrivateFileToPublicAlert
          {...privateToPublicMoveAlert}
          isLoading={isMoveInProgress}
          moveFileToPublic={moveItems}
        />
      )}
    </>
  );
};
